import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  X,
  UserPlus,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Search,
  User,
  Package,
  ArrowDown,
  ArrowUp,
  Eye,
  EyeOff,
  Link2,
  X as XIcon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const CreatePppoeSecretModal = ({ routerId, routerName, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: "",
    password: "",
    profile: "",
    service: "pppoe",
    comment: "",
  });
  const [showPass, setShowPass] = useState(false);

  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [profileError, setProfileError] = useState("");

  const [pelSearch, setPelSearch] = useState("");
  const [pelResults, setPelResults] = useState([]);
  const [selectedPel, setSelectedPel] = useState(null);
  const [pelSearchOpen, setPelSearchOpen] = useState(false);
  const [pelLoading, setPelLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  // Load PPPoE profiles from router on mount
  useEffect(() => {
    const loadProfiles = async () => {
      setLoadingProfiles(true);
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/jaringan/mikrotik/routers/${routerId}/pppoe/profiles`,
          { withCredentials: true }
        );
        setProfiles(res.data.profiles || []);
      } catch (err) {
        setProfileError(err.response?.data?.detail || "Gagal fetch profile dari router");
      } finally {
        setLoadingProfiles(false);
      }
    };
    loadProfiles();
  }, [routerId]);

  // Debounced pelanggan search
  useEffect(() => {
    if (!pelSearchOpen) return;
    const t = setTimeout(async () => {
      setPelLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/pelanggan/search`, {
          params: { q: pelSearch, limit: 15 },
          withCredentials: true,
        });
        setPelResults(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setPelLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [pelSearch, pelSearchOpen]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedProfile = profiles.find((p) => p.name === form.profile);

  const handleSelectPelanggan = (pel) => {
    setSelectedPel(pel);
    setPelSearchOpen(false);
    setPelSearch("");
    // Auto-fill username if empty
    if (!form.name && pel.username_pppoe) {
      setField("name", pel.username_pppoe);
    } else if (!form.name && pel.no_layanan) {
      setField("name", pel.no_layanan.toLowerCase().replace(/[^a-z0-9]/g, "_"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(null);
    if (!form.profile) {
      setError("Pilih PPPoE profile terlebih dahulu");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        password: form.password,
        profile: form.profile,
        service: form.service,
        comment: form.comment,
      };
      if (selectedPel) payload.pelanggan_id = selectedPel.id;
      const res = await axios.post(
        `${BACKEND_URL}/api/jaringan/mikrotik/routers/${routerId}/pppoe/secrets`,
        payload,
        { withCredentials: true }
      );
      setSuccess(res.data);
      setTimeout(() => {
        onSuccess?.(res.data);
        onClose();
      }, 1600);
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal membuat secret PPPoE");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="create-secret-modal"
    >
      <div className="w-full max-w-lg rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex justify-between items-start">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <UserPlus className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Buat Secret PPPoE</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Router: <span className="text-cyan-400 font-mono">{routerName}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white flex items-center justify-center"
            data-testid="close-create-secret-modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {success && (
            <div
              className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs flex items-start gap-2"
              data-testid="create-secret-success"
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">{success.message}</p>
                {success.linked_pelanggan && (
                  <p className="text-emerald-400/80 mt-1 text-[11px]">
                    Terhubung ke: <span className="font-mono">{success.linked_pelanggan.nama}</span>
                  </p>
                )}
              </div>
            </div>
          )}

          {error && (
            <div
              className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-start gap-2"
              data-testid="create-secret-error"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Terhubung ke Pelanggan */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1.5 flex items-center gap-1">
              <Link2 className="w-3 h-3 text-cyan-400" />
              Terhubung ke Pelanggan <span className="text-slate-500 font-normal">(opsional)</span>
            </label>

            {selectedPel ? (
              <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-cyan-200 truncate">{selectedPel.nama}</p>
                  <p className="text-[10px] font-mono text-cyan-400/80">
                    {selectedPel.no_layanan} • {selectedPel.no_hp}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPel(null)}
                  className="w-5 h-5 rounded text-cyan-300 hover:bg-cyan-500/20 flex items-center justify-center flex-shrink-0"
                  data-testid="clear-pelanggan-btn"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={pelSearch}
                  onChange={(e) => {
                    setPelSearch(e.target.value);
                    setPelSearchOpen(true);
                  }}
                  onFocus={() => setPelSearchOpen(true)}
                  placeholder="Cari nama pelanggan / no. layanan..."
                  className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid="pelanggan-search-input"
                />

                {pelSearchOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setPelSearchOpen(false)}
                    />
                    <div
                      className="absolute z-50 mt-1 w-full rounded-xl bg-[#0e1628] border border-slate-700 shadow-2xl max-h-56 overflow-y-auto"
                      data-testid="pelanggan-search-results"
                    >
                      {pelLoading ? (
                        <div className="p-3 text-center text-xs text-slate-500">
                          <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Mencari...
                        </div>
                      ) : pelResults.length === 0 ? (
                        <div className="p-3 text-center text-xs text-slate-500">
                          {pelSearch ? "Tidak ditemukan pelanggan" : "Ketik untuk mencari pelanggan"}
                        </div>
                      ) : (
                        pelResults.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => handleSelectPelanggan(p)}
                            className="w-full px-3 py-2 text-left hover:bg-slate-800/60 border-b border-slate-800/60 last:border-b-0"
                            data-testid={`pelanggan-option-${p.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-white truncate">{p.nama}</p>
                                <p className="text-[10px] font-mono text-slate-400 truncate">
                                  {p.no_layanan} • {p.no_hp}
                                  {p.paket_nama && ` • ${p.paket_nama}`}
                                </p>
                              </div>
                              <span
                                className={`text-[9px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                                  p.status_layanan === "aktif"
                                    ? "bg-emerald-500/20 text-emerald-300"
                                    : "bg-slate-800 text-slate-400"
                                }`}
                              >
                                {(p.status_layanan || "-").toUpperCase()}
                              </span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Username & Password */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1.5">Username PPPoE</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="ahmad_fauzi"
                autoComplete="off"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="secret-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  required
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 pr-9 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  data-testid="secret-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPass ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Profile Dropdown */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1.5 flex items-center gap-1">
              <Package className="w-3 h-3 text-emerald-400" />
              Profil PPPoE (dari Mikrotik)
            </label>
            {loadingProfiles ? (
              <div className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-500 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin text-cyan-400" />
                Memuat profile dari router...
              </div>
            ) : profileError ? (
              <div className="w-full px-3 py-2 bg-rose-500/10 border border-rose-500/40 rounded-xl text-xs text-rose-300">
                {profileError}
              </div>
            ) : (
              <select
                required
                value={form.profile}
                onChange={(e) => setField("profile", e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="secret-profile-select"
              >
                <option value="">-- Pilih Profile --</option>
                {profiles.map((p) => (
                  <option key={p.id || p.name} value={p.name}>
                    {p.name}
                    {p.rate_limit ? ` — ${p.rate_limit}` : ""}
                  </option>
                ))}
              </select>
            )}

            {/* Auto-filled speed info */}
            {selectedProfile && (
              <div className="mt-2 p-2.5 rounded-lg bg-slate-900/60 border border-slate-800 grid grid-cols-2 gap-2 text-xs" data-testid="profile-speed-info">
                <div className="flex items-center gap-2">
                  <ArrowDown className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-slate-400 text-[11px]">Download:</span>
                  <span className="font-mono font-bold text-cyan-300">
                    {selectedProfile.download || "unlimited"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <ArrowUp className="w-3.5 h-3.5 text-indigo-400" />
                  <span className="text-slate-400 text-[11px]">Upload:</span>
                  <span className="font-mono font-bold text-indigo-300">
                    {selectedProfile.upload || "unlimited"}
                  </span>
                </div>
                {selectedProfile.rate_limit && (
                  <div className="col-span-2 text-[10px] text-slate-500 font-mono border-t border-slate-800 pt-1.5 mt-1">
                    rate-limit: {selectedProfile.rate_limit}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Service */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1.5">Service</label>
            <select
              value={form.service}
              onChange={(e) => setField("service", e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="secret-service-select"
            >
              <option value="pppoe">pppoe</option>
              <option value="any">any</option>
              <option value="pptp">pptp</option>
              <option value="l2tp">l2tp</option>
              <option value="sstp">sstp</option>
              <option value="ovpn">ovpn</option>
            </select>
          </div>

          {/* Comment */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1.5">Catatan (opsional)</label>
            <input
              type="text"
              value={form.comment}
              onChange={(e) => setField("comment", e.target.value)}
              placeholder={
                selectedPel
                  ? `Auto: Pelanggan: ${selectedPel.nama} | ${selectedPel.no_layanan}`
                  : "Contoh: pelanggan area timur, install 2024-01-01"
              }
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="secret-comment-input"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 disabled:opacity-50"
              data-testid="cancel-create-secret-btn"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading || !!success || loadingProfiles}
              className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/30"
              data-testid="submit-create-secret-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Membuat secret...</span>
                </>
              ) : success ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Berhasil</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  <span>Buat Secret PPPoE</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
